import axios from "../api";

const toNum = (v) => (v == null || v === "" ? 0 : Number(v));

/**
 * Fecha YYYY-MM-DD del día anterior a la consultada.
 */
export function fechaDiaAnterior(fechaInicial) {
  const d = new Date(`${fechaInicial}T12:00:00`);
  if (Number.isNaN(d.getTime())) {
    throw new Error("Fecha inválida para validar día anterior.");
  }
  d.setDate(d.getDate() - 1);
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

/**
 * Genera un id_inicio_dia aleatorio grande (ej. 2000001, 10000001, 60048942).
 */
export function generarIdInicioDiaAleatorio(excluir = new Set()) {
  let candidato;
  let intentos = 0;
  do {
    candidato = Math.floor(1_000_000 + Math.random() * 99_000_000);
    intentos += 1;
  } while (excluir.has(candidato) && intentos < 50);
  return candidato;
}

/**
 * Si el id_inicio_dia del día consultado coincide con el del día anterior,
 * genera uno aleatorio nuevo para el día consultado.
 * @returns {{ idInicioDia: number|null, cambiado: boolean, idAnterior: number|null, fechaAnterior: string }}
 */
export async function resolverIdInicioDiaVsDiaAnterior({
  codTerminal,
  codPark,
  fechaInicial,
  idInicioDiaActual,
  headers = {},
}) {
  const idActual =
    idInicioDiaActual == null || idInicioDiaActual === ""
      ? null
      : Number(idInicioDiaActual);
  const fechaAnterior = fechaDiaAnterior(fechaInicial);

  if (idActual == null || Number.isNaN(idActual)) {
    return {
      idInicioDia: idInicioDiaActual ?? null,
      cambiado: false,
      idAnterior: null,
      fechaAnterior,
    };
  }

  try {
    const { data } = await axios.post(
      `/cierres-dias/buscar`,
      {
        codTerminal,
        codPark,
        fechaInicial: fechaAnterior,
      },
      { headers }
    );

    const diasPrevios = Array.isArray(data) ? data : [];
    const idsPrevios = new Set(
      diasPrevios
        .map((d) => Number(d.idInicioDia))
        .filter((n) => !Number.isNaN(n))
    );

    if (idsPrevios.has(idActual)) {
      const nuevo = generarIdInicioDiaAleatorio(
        new Set([...idsPrevios, idActual])
      );
      return {
        idInicioDia: nuevo,
        cambiado: true,
        idAnterior: idActual,
        fechaAnterior,
      };
    }
  } catch (error) {
    // Si no hay cierre del día anterior o falla la consulta, se conserva el id actual.
    console.warn(
      "No se pudo validar id_inicio_dia del día anterior:",
      error?.message || error
    );
  }

  return {
    idInicioDia: idActual,
    cambiado: false,
    idAnterior: null,
    fechaAnterior,
  };
}

/**
 * Agregación de sumas para cierre de día.
 * Con un solo turno: factura_ini / factura_fin del turno (pueden ser 0).
 * Con varios turnos: mínimo de factura_ini y máximo de factura_fin, excluyendo ceros.
 */
export function computeSumasDeTurnos(cierreTurnoData) {
  if (!cierreTurnoData?.length) return null;

  let facturaIni;
  let facturaFin;
  if (cierreTurnoData.length === 1) {
    const t = cierreTurnoData[0];
    facturaIni = toNum(t.facturaIni);
    facturaFin = toNum(t.facturaFin);
  } else {
    const facturasIniPos = cierreTurnoData
      .map((t) => toNum(t.facturaIni))
      .filter((n) => n > 0);
    const facturasFinPos = cierreTurnoData
      .map((t) => toNum(t.facturaFin))
      .filter((n) => n > 0);
    facturaIni = facturasIniPos.length ? Math.min(...facturasIniPos) : null;
    facturaFin = facturasFinPos.length ? Math.max(...facturasFinPos) : null;
  }

  return {
    total: cierreTurnoData.reduce((s, t) => s + toNum(t.total), 0),
    subtotal: cierreTurnoData.reduce((s, t) => s + toNum(t.subtotal), 0),
    totalEfectivoDia: cierreTurnoData.reduce((s, t) => s + toNum(t.reportado), 0),
    totalEfectivo: cierreTurnoData.reduce((s, t) => s + toNum(t.totalEfectivo), 0),
    totalTc: cierreTurnoData.reduce((s, t) => s + toNum(t.totalTc), 0),
    totalMensualesEf: cierreTurnoData.reduce((s, t) => s + toNum(t.totalMensualesEf), 0),
    totalMensualesTc: cierreTurnoData.reduce((s, t) => s + toNum(t.totalMensualesTc), 0),
    ivaEfectivo: cierreTurnoData.reduce((s, t) => s + toNum(t.ivaEfectivo), 0),
    ivaDatafono: cierreTurnoData.reduce((s, t) => s + toNum(t.ivaDatafono), 0),
    totalNotasCredito: cierreTurnoData.reduce((s, t) => s + toNum(t.notasCredito), 0),
    facturaIni,
    facturaFin,
  };
}

/**
 * Replica el botón "Pasar sumas de turnos a cierre de día" de Trasmitir Global.
 * Antes de guardar, valida id_inicio_dia contra el día anterior; si coinciden,
 * asigna uno aleatorio al día consultado.
 * @returns Lista actualizada de cierres de día tras el POST /buscar.
 */
export async function pasarSumasTurnosACierreDiaApi({
  cierreTurnoData,
  cierreDiaData,
  codTerminal,
  codPark,
  fechaInicial,
  headers = {},
  validarDiaAnterior = true,
}) {
  const sumasDeTurnos = computeSumasDeTurnos(cierreTurnoData);
  if (!sumasDeTurnos || !cierreDiaData?.length) {
    throw new Error(
      "No hay datos suficientes para aplicar sumas (faltan turnos o cierre de día)."
    );
  }

  // Cierre de día: estado 1 si todos los turnos están en 1; estado 2 si alguno está en 2.
  const estadoCierreDia = cierreTurnoData.some((t) => Number(t.estado) === 2)
    ? "2"
    : "1";

  // Preferimos id_inicio_dia del cierre de día consultado; si no, el de turnos.
  const idInicioDiaActual =
    cierreDiaData[0]?.idInicioDia ?? cierreTurnoData[0]?.idInicioDia ?? null;

  let idInicioDiaFinal = idInicioDiaActual;
  let avisoCambioId = null;

  if (validarDiaAnterior) {
    const resolucion = await resolverIdInicioDiaVsDiaAnterior({
      codTerminal,
      codPark,
      fechaInicial,
      idInicioDiaActual,
      headers,
    });
    idInicioDiaFinal = resolucion.idInicioDia;
    if (resolucion.cambiado) {
      avisoCambioId =
        `El id_inicio_dia ${resolucion.idAnterior} ya existía en el día anterior (${resolucion.fechaAnterior}). ` +
        `Se asignó el nuevo id_inicio_dia ${resolucion.idInicioDia} al cierre de día y a los turnos del día consultado (${fechaInicial}).`;
    }
  }

  const payload = {
    ...sumasDeTurnos,
    turnos: cierreTurnoData.length,
    idInicioDia: idInicioDiaFinal,
    idPlanilla: "-",
    transmitido: false,
    estado: estadoCierreDia,
  };

  for (const row of cierreDiaData) {
    await axios.patch(`/cierres-dias/${row.idCierreDia}`, payload, { headers });
  }

  // Si se generó un nuevo id (o difiere del de los turnos), actualizar también cierre_turno.
  const turnosAActualizar = cierreTurnoData.filter(
    (t) => t?.id != null && Number(t.idInicioDia) !== Number(idInicioDiaFinal)
  );
  for (const turno of turnosAActualizar) {
    await axios.patch(
      `/cierre-turno/${turno.id}`,
      { idInicioDia: idInicioDiaFinal },
      { headers }
    );
  }

  if (avisoCambioId && turnosAActualizar.length > 0) {
    avisoCambioId += ` Se actualizaron ${turnosAActualizar.length} turno(s).`;
  }

  const [{ data: dataDia }, { data: dataTurno }] = await Promise.all([
    axios.post(
      `/cierres-dias/buscar`,
      { codTerminal, codPark, fechaInicial },
      { headers }
    ),
    axios.post(
      `/cierre-turno/filtrar`,
      { codTerminal, codPark, fechaInicial },
      { headers }
    ),
  ]);

  const lista = Array.isArray(dataDia) ? dataDia : [];
  if (avisoCambioId) {
    lista._avisoIdInicioDia = avisoCambioId;
  }
  lista._cierreTurnoData = Array.isArray(dataTurno) ? dataTurno : [];
  return lista;
}
